import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import {
  removeTab,
  setActiveTab,
  setLastSelectedLocation,
  setLastSelectedWarehouse,
} from "../../../../../redux/slices/appointmentsSlices";
import {
  fetchLocationByRef,
  fetchDockById,
  fetchWarehouseSlotsAvailability,
  getShipmentByShipmentNum,
} from "../api/AppointmentsApi";
import notify from "../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";

// ─── useAppointmentMode ───────────────────────────────────────────────────────
/**
 * Single source of truth for all mode-derived flags.
 *
 * Mode resolution order (highest → lowest priority):
 *   1. Carrier viewing a drop trailer appt → forced read-only
 *   2. shipment is "Active" in-transit      → forced read-only
 *   3. explicit mode prop === "readonly"    → read-only
 *   4. mode === "add"                       → add (edit)
 *   5. mode === "update" (default)          → update (edit)
 *
 * Carriers may only view (never add/edit) drop trailer appointments, so when
 * userType is "Carrier" and the appointment is a drop trailer the entire form
 * is locked to read-only.
 */
export function useAppointmentMode(mode, shipmentData, { userType, isDropTrailer } = {}) {
  const isCarrierDropTrailer = userType === "Carrier" && Boolean(isDropTrailer);
  const isAddMode      = mode === "add" && !isCarrierDropTrailer;
  const isReadOnlyMode = mode === "readonly" || shipmentData?.status === "Active" || isCarrierDropTrailer;
  const isEditMode     = !isReadOnlyMode;
  return { isAddMode, isReadOnlyMode, isEditMode, isCarrierDropTrailer };
}

// ─── useAppointmentData ───────────────────────────────────────────────────────
/**
 * Owns all remote-data fetching: location / warehouse / dock lookups,
 * available shipments list, shipment detail, and persisting last-selected
 * location + warehouse to Redux.
 */
export function useAppointmentData({ appointmentData, userType, isEditMode, isShipper }) {
  const dispatch   = useDispatch();
  const officeCode = Cookies.get("officeCode");
  const { lastSelectedLocation, lastSelectedWarehouse } = useSelector((s) => s.appointments);

  const [locationQuery,    setLocationQuery]    = useState("");
  const [locationOptions,  setLocationOptions]  = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [warehouseQuery,     setWarehouseQuery]     = useState("");
  const [warehouseOptions,   setWarehouseOptions]   = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [selectedWarehouse,  setSelectedWarehouse]  = useState(null);

  const [dockQuery,     setDockQuery]     = useState("");
  const [dockOptions,   setDocksOptions]  = useState([]);
  const [selectedDocks, setSelectedDocks] = useState(null);

  const [doorQuery,    setDoorQuery]    = useState("");
  const [doorOptions,  setDoorOptions]  = useState([]);
  const [selectedDoor, setSelectedDoor] = useState(null);

  const [shipmentData,       setShipmentData]       = useState(null);
  const [availableShipments, setAvailableShipments] = useState([]);

  const [appointmentCarrier, setAppointmentCarrier] = useState({
    driverName:    appointmentData?.driverName    || "",
    carrierName:   appointmentData?.carrierName   || "",
    trailerNumber: appointmentData?.trailerNumber || "",
  });

  // Persist selection to Redux
  useEffect(() => {
    if (!selectedLocation) return;
    dispatch(setLastSelectedLocation({
      id:           selectedLocation.id || selectedLocation.uid,
      locationRef:  selectedLocation.locationRef || selectedLocation.id,
      locationName: selectedLocation.locationName,
    }));
  }, [selectedLocation, dispatch]);

  useEffect(() => {
    if (!selectedWarehouse) return;
    dispatch(setLastSelectedWarehouse({
      id:           selectedWarehouse.id || selectedWarehouse.uid || selectedWarehouse.locationRef,
      locationRef:  selectedWarehouse.locationRef,
      locationName: selectedWarehouse.locationName,
    }));
  }, [selectedWarehouse, dispatch]);

  // Restore last-selected location (add mode)
  useEffect(() => {
    if (appointmentData || !lastSelectedLocation || selectedLocation || !locationOptions.length) return;
    const matched = locationOptions.find(
      (loc) =>
        loc.uid          === lastSelectedLocation.id ||
        loc.id           === lastSelectedLocation.id ||
        loc.locationName === lastSelectedLocation.locationName
    );
    if (matched) { setSelectedLocation(matched); setLocationQuery(matched.locationName); }
  }, [appointmentData, lastSelectedLocation, selectedLocation, locationOptions]);

  // Restore last-selected warehouse (add mode)
  useEffect(() => {
    if (appointmentData || !selectedLocation || !lastSelectedWarehouse || selectedWarehouse || !warehouseOptions.length) return;
    const matched = warehouseOptions.find(
      (wh) =>
        wh.locationRef  === lastSelectedWarehouse.locationRef ||
        wh.locationName === lastSelectedWarehouse.locationName
    );
    if (matched) { setSelectedWarehouse(matched); setWarehouseQuery(matched.locationName); }
  }, [appointmentData, selectedLocation, lastSelectedWarehouse, selectedWarehouse, warehouseOptions]);

  // Fetch location by ref (update/view mode)
  useEffect(() => {
    if (!appointmentData?.location) return;
    fetchLocationByRef(appointmentData.location)
      .then((loc) => { if (loc) { setSelectedLocation(loc); setLocationQuery(loc.locationName); } })
      .catch((err) => console.error("Failed to fetch location:", err));
  }, [appointmentData?.location]);

  // Fetch location list (Shipper add mode)
  useEffect(() => {
    if (appointmentData?.location || userType !== "Shipper" || isEditMode) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/locations/by-office-code/${officeCode}`)
      .then((res) => setLocationOptions(res.data || []))
      .catch(() => setLocationOptions([]));
  }, [appointmentData?.location, userType, isEditMode, officeCode]);

  // Fetch warehouse by ref
  useEffect(() => {
    if (!appointmentData?.warehouse) return;
    fetchLocationByRef(appointmentData.warehouse)
      .then((wh) => {
        if (wh && typeof wh === "object") {
          setWarehouseOptions([wh]);
          setSelectedWarehouse(wh);
          setWarehouseQuery(wh.locationName);
        }
      })
      .catch((err) => console.error("Failed to fetch warehouse:", err));
  }, [appointmentData?.warehouse]);

  // Fetch warehouse list when location is selected
  useEffect(() => {
    if (!selectedLocation?.id) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/locations/children/by-id?parentId=${selectedLocation.id}`)
      .then((res) => {
        setWarehouseOptions(res.data);
        setFilteredWarehouses(res.data);
        const matched = res.data.find(
          (wh) => wh.customLocationIdentifier === appointmentData?.customLocationIdentifier
        );
        if (matched) {
          setSelectedWarehouse(matched);
          setWarehouseQuery(matched.locationName);
        } else if (!isEditMode && !appointmentData?.warehouse) {
          setSelectedWarehouse(null);
          setWarehouseQuery("");
        }
      })
      .catch(() => { setWarehouseOptions([]); setFilteredWarehouses([]); });
  }, [selectedLocation, appointmentData?.customLocationIdentifier, appointmentData?.warehouse, isEditMode]);

  // Fetch dock by id
  useEffect(() => {
    const dockId = Number(appointmentData?.dockNumber);
    if (!dockId || isNaN(dockId)) return;
    fetchDockById(dockId)
      .then((dock) => {
        if (dock) { setDocksOptions([dock]); setSelectedDocks(dock); setDockQuery(dock.dockName); }
      })
      .catch(() => setDocksOptions([]));
  }, [appointmentData?.dockNumber]);

  // Fetch docks when warehouse is selected
  useEffect(() => {
    if (!selectedWarehouse?.locationRef) { setDocksOptions([]); setSelectedDocks(null); return; }
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/dock/docks/${selectedWarehouse.locationRef}`)
      .then((res) => {
        setDocksOptions(res.data || []);
        if (userType === "Carrier" && res.data.length > 0 && !isEditMode) {
          setSelectedDocks(res.data[0]);
          setDockQuery(res.data[0].dockName || "");
        }
      })
      .catch(() => setDocksOptions([]));
  }, [selectedWarehouse, userType, isEditMode]);

  // Auto-select matching dock in edit/update mode
  useEffect(() => {
    if (!isEditMode || !appointmentData || !dockOptions.length || selectedDocks) return;
    const matched = dockOptions.find((d) => String(d.dockId) === String(appointmentData.dockNumber));
    if (matched) { setSelectedDocks(matched); setDockQuery(matched.dockName || ""); }
  }, [dockOptions, appointmentData, isEditMode, selectedDocks]);

  // Fetch available shipments (Shipper only)
  useEffect(() => {
    if (userType !== "Shipper") return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/shipments/appointment-details`, {
        params: { officeCode, page: 0, size: 10 },
      })
      .then((res) => {
        const seen = new Set();
        setAvailableShipments(
          res.data.content.filter((s) => {
            if (seen.has(s.shipmentNumber)) return false;
            seen.add(s.shipmentNumber);
            return true;
          })
        );
      })
      .catch(() => {});
  }, [userType, officeCode]);

  // Merge appointment's own shipment into the available list
  useEffect(() => {
    if (!appointmentData?.shipmentNumber) return;
    const fromAppointment = {
      shipmentNumber: appointmentData.shipmentNumber,
      partyName:      appointmentData.company || appointmentData.shipperName,
      addressLine1:   appointmentData.addressLine1     || "",
      addressLine2:   appointmentData.addressLine2     || "",
      city:           appointmentData.city             || "",
      zipCode:        appointmentData.zipCode          || "",
      country:        appointmentData.country          || "",
      state:          appointmentData.state            || "",
      officeCode:     appointmentData.carrierOfficeCode|| "",
      stopReasonCode: appointmentData.stopReasonCode   || "",
      poNumber:       appointmentData.poNumber         || "",
      saleNumber:     appointmentData.saleNumber       || "",
    };
    setAvailableShipments((prev) =>
      prev.some((s) => s.shipmentNumber === appointmentData.shipmentNumber)
        ? prev
        : [fromAppointment, ...prev]
    );
  }, [appointmentData]);

  // Fetch shipment detail
  useEffect(() => {
    if (!appointmentData?.shipmentNumber) return;
    const oc =
      appointmentData.shipperOfficeCode  ||
      appointmentData.officeCodeShipper  ||
      appointmentData.shipmentOfficeCode ||
      Cookies.get("officeCode");
    getShipmentByShipmentNum(appointmentData.shipmentNumber, oc)
      .then((data) => { if (data) setShipmentData(data); })
      .catch((err) => console.error("Failed to fetch shipment data:", err));
  }, [appointmentData?.shipmentNumber]);

  return {
    locationQuery, setLocationQuery, locationOptions,
    selectedLocation, setSelectedLocation,
    warehouseQuery, setWarehouseQuery, warehouseOptions, filteredWarehouses,
    selectedWarehouse, setSelectedWarehouse,
    dockQuery, setDockQuery, dockOptions,
    selectedDocks, setSelectedDocks,
    doorQuery, setDoorQuery, doorOptions,
    selectedDoor, setSelectedDoor,
    shipmentData, availableShipments,
    appointmentCarrier, setAppointmentCarrier,
  };
}

// ─── useSlots ─────────────────────────────────────────────────────────────────
/**
 * Owns all slot-related state and async logic. Keeps race-condition
 * protection via fetchId refs so stale responses are silently dropped.
 */
export function useSlots({ date, selectedWarehouse, selectedDocks, dockOptions, isReadOnlyMode, isEditMode, isCancelled, appointmentData }) {
  const slotsFetchIdRef    = useRef(0);
  const allSlotsFetchIdRef = useRef(0);

  const [slots,              setSlots]              = useState([]);
  const [selectedSlot,       setSelectedSlot]       = useState(null);
  const [allDockSlots,       setAllDockSlots]       = useState([]);
  const [isAllSlotsLoading,  setIsAllSlotsLoading]  = useState(false);
  const [allSlotsError,      setAllSlotsError]      = useState(null);
  const [showAllSlotsModal,  setShowAllSlotsModal]  = useState(false);
  const [showInlineAllSlots, setShowInlineAllSlots] = useState(true);

  const fetchAndMapDockSlots = useCallback(async (locationRef, dateStr, fetchId, fetchIdRef) => {
    try {
      const data = await fetchWarehouseSlotsAvailability(locationRef, dateStr);
      if (fetchId !== fetchIdRef.current) return null;
      return (data || []).map((item) => {
        const matchedDock = dockOptions.find((d) => String(d.dockId) === String(item.dockId));
        const dock = matchedDock || { dockId: item.dockId, dockName: item.dockName, locationRef: item.locationRefId };
        return { dock, slots: Array.isArray(item.slots) ? item.slots : [] };
      });
    } catch (error) {
      if (fetchId === fetchIdRef.current) console.error("Error fetching dock slots:", error);
      throw error;
    }
  }, [dockOptions]);

  const getSlotsForDock = useCallback((mappedData, dockId) => {
    if (!dockId || !mappedData) return [];
    return mappedData.find(({ dock }) => String(dock.dockId) === String(dockId))?.slots ?? [];
  }, []);

  const handleFindSlots = useCallback(async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (isEditMode && new Date(date) < today && selectedSlot) {
      notify.warning({ title: "Invalid Date", message: "Appointment cannot be scheduled to a past date." });
      return;
    }
    if (!selectedWarehouse?.locationRef || !selectedDocks?.dockId) return;
    const fetchId = ++slotsFetchIdRef.current;
    try {
      const mapped = await fetchAndMapDockSlots(selectedWarehouse.locationRef, date, fetchId, slotsFetchIdRef);
      if (!mapped) return;
      setAllDockSlots(mapped);
      const available = getSlotsForDock(mapped, selectedDocks.dockId);
      setSlots(available);
      if (isEditMode && appointmentData?.startTime) {
        const savedStart   = appointmentData.startTime.split("T")[1]?.substring(0, 5);
        const matchingSlot = available.find((s) => s.startTime === savedStart);
        if (matchingSlot) setSelectedSlot(matchingSlot);
      }
    } catch {
      if (fetchId === slotsFetchIdRef.current) setSlots([]);
    }
  }, [date, selectedWarehouse, selectedDocks, isEditMode, appointmentData, fetchAndMapDockSlots, getSlotsForDock, selectedSlot]);

  const fetchAllDockSlots = useCallback(async () => {
    if (isCancelled || !date || !selectedWarehouse?.locationRef) return;
    const fetchId = ++allSlotsFetchIdRef.current;
    setIsAllSlotsLoading(true);
    setAllSlotsError(null);
    try {
      const mapped = await fetchAndMapDockSlots(selectedWarehouse.locationRef, date, fetchId, allSlotsFetchIdRef);
      if (!mapped) return;
      setAllDockSlots(mapped);
      if (selectedDocks?.dockId) setSlots(getSlotsForDock(mapped, selectedDocks.dockId));
    } catch (err) {
      if (fetchId === allSlotsFetchIdRef.current) {
        setAllDockSlots([]);
        setAllSlotsError(err?.response?.data?.message || err?.message || "Unable to load slots.");
      }
    } finally {
      if (fetchId === allSlotsFetchIdRef.current) setIsAllSlotsLoading(false);
    }
  }, [date, isCancelled, selectedWarehouse?.locationRef, selectedDocks?.dockId, fetchAndMapDockSlots, getSlotsForDock]);

  const refreshAndValidateSelectedSlot = useCallback(async () => {
    if (!date || !selectedWarehouse?.locationRef || !selectedDocks?.dockId || !selectedSlot) return true;
    const fetchId = ++slotsFetchIdRef.current;
    try {
      const mapped = await fetchAndMapDockSlots(selectedWarehouse.locationRef, date, fetchId, slotsFetchIdRef);
      if (!mapped) return true;
      setAllDockSlots(mapped);
      const available = getSlotsForDock(mapped, selectedDocks.dockId);
      setSlots(available);
      const stillAvailable = available.some(
        (s) => s.startTime === selectedSlot.startTime && s.endTime === selectedSlot.endTime
      );
      if (!stillAvailable) {
        setSelectedSlot(null);
        notify.error({ title: "Slot Unavailable", message: "The selected slot was just taken. Please pick another." });
      }
      return stillAvailable;
    } catch {
      return true;
    }
  }, [date, selectedWarehouse?.locationRef, selectedDocks?.dockId, selectedSlot, fetchAndMapDockSlots, getSlotsForDock]);

  const handleSelectInlineSlot = useCallback((dock, slot, slotsForDock, { setSelectedDocks, setDockQuery }) => {
    if (isReadOnlyMode) return;
    setSelectedDocks(dock);
    setDockQuery(dock?.dockName || "");
    setSelectedSlot(slot);
    if (Array.isArray(slotsForDock)) setSlots(slotsForDock);
  }, [isReadOnlyMode]);

  const handleSelectSlotFromModal = useCallback((dock, slot, { setSelectedDocks, setDockQuery }) => {
    if (isReadOnlyMode) return;
    const [year, month, day] = date.split("-").map(Number);
    const [hours, minutes]   = slot.startTime.split(":").map(Number);
    if (new Date(year, month - 1, day, hours, minutes) < new Date()) {
      notify.warning({ title: "Invalid Time Slot", message: "Cannot select a time slot in the past." });
      return;
    }
    setSelectedDocks(dock);
    setDockQuery(dock?.dockName || "");
    setSelectedSlot(slot);
    setShowAllSlotsModal(false);
  }, [isReadOnlyMode, date]);

  return {
    slots, selectedSlot, setSelectedSlot,
    allDockSlots, isAllSlotsLoading, allSlotsError,
    showAllSlotsModal, setShowAllSlotsModal,
    showInlineAllSlots, setShowInlineAllSlots,
    handleFindSlots, fetchAllDockSlots,
    refreshAndValidateSelectedSlot,
    handleSelectInlineSlot, handleSelectSlotFromModal,
  };
}

// ─── useAppointmentSubmit ─────────────────────────────────────────────────────
/**
 * Encapsulates submit/update/add logic. Accepts stable references from
 * sibling hooks — never reaches into component state directly.
 */
export function useAppointmentSubmit({
  isAddMode, isReadOnlyMode, isCancelled, appointmentData,
  date, selectedLocation, selectedWarehouse, selectedDocks, selectedDoor,
  selectedSlot, shipments, selectedShipmentNumber, appointmentCarrier,
  shipmentData, userType, refreshAndValidateSelectedSlot, resetForm,
}) {
  const dispatch   = useDispatch();
  const officeCode = Cookies.get("officeCode");
  const { activeTab } = useSelector((s) => s.appointments);
  const [loading, setLoading] = useState(false);

  const extractStopReasonFromShipmentData = (data, stopNumberHint) => {
    if (!data?.shipmentStops?.length) return null;
    const stopNum = Number(stopNumberHint ?? appointmentData?.stopNumber);
    const matched =
      data.shipmentStops.find(
        (s) =>
          (s.stopSequence != null && Number(s.stopSequence) === stopNum) ||
          (s.stopNumber   != null && Number(s.stopNumber)   === stopNum)
      ) || data.shipmentStops[0];
    return matched?.stopReasonCode || matched?.stopReasonName || null;
  };

  const resolveRequestType = (selectedShipment) => {
    const stopReasonFromStops = extractStopReasonFromShipmentData(
      shipmentData,
      selectedShipment?.stopNumber ?? appointmentData?.stopNumber
    );
    const rawStopReason = (
      selectedShipment.stopReasonCode ||
      appointmentData?.stopReasonCode  ||
      shipmentData?.stopReasonCode     ||
      stopReasonFromStops              ||
      ""
    ).trim().toUpperCase();
    const isDropTrailer =
      rawStopReason === "DT" || rawStopReason === "DROP TRAILER" || rawStopReason === "DROPTRAILER" ||
      (rawStopReason.includes("DROP") && rawStopReason.includes("TRAILER"));
    return { rawStopReason, isDropTrailer, requestType: isDropTrailer ? "DROP_TRAILER" : "LIVE_LOAD" };
  };

  const handleSubmit = async () => {
    if (isReadOnlyMode) return;
    if (isCancelled) {
      notify.warning({ title: "Appointment Cancelled", message: "This appointment cannot be updated." });
      return;
    }
    if (!selectedSlot) {
      notify.warning({ title: "Missing Selection", message: "Please select a time slot before proceeding." });
      return;
    }
    const selectedShipment = selectedShipmentNumber
      ? shipments.find((s) => s.shipmentNumber === selectedShipmentNumber)
      : shipments[0];
    if (!selectedShipment) {
      notify.warning({ title: "No Shipment Selected", message: "Please choose a shipment before continuing." });
      return;
    }

    setLoading(true);
    try {
      const { rawStopReason, isDropTrailer, requestType } = resolveRequestType(selectedShipment);
      if (userType === "Carrier" && isDropTrailer) {
        notify.warning({
          title: "Read-Only Appointment",
          message: "Carriers can only view drop trailer appointments and cannot add or edit them.",
        });
        return;
      }
      const payload = {
        startTime:         `${date}T${selectedSlot.startTime}`,
        endTime:           `${date}T${selectedSlot.endTime}`,
        location:          selectedLocation.locationRef,
        warehouse:         selectedWarehouse.locationRef,
        dockNumber:        selectedDocks.dockId,
        doorNumber:        selectedDoor?.doorId,
        driverName:        appointmentCarrier.driverName,
        trailerNumber:     appointmentCarrier.trailerNumber,
        shipmentNumber:    selectedShipment.shipmentNumber,
        stopNumber:        selectedShipment.stopNumber,
        companyName:       selectedShipment.company,
        addressLine1:      selectedShipment.addressLine1,
        addressLine2:      selectedShipment.addressLine2,
        city:              selectedShipment.city,
        zipCode:           selectedShipment.zipCode,
        country:           selectedShipment.country,
        state:             selectedShipment.state,
        officeCodeShipper: selectedShipment?.officeCodeShipper,
        officeCodeCarrier: userType === "Carrier" ? officeCode : selectedShipment.officeCodeCarrier,
        carrierName:       selectedShipment.carrierName,
        poNumber:          selectedShipment.poNumber,
        saleNumber:        selectedShipment.saleNumber,
        stopReasonCode:    rawStopReason,
        requestType,
        isDropTrailer,
      };

      const slotStillFree = await refreshAndValidateSelectedSlot();
      if (!slotStillFree) return;

      const token = Cookies.get("authToken");
      if (isAddMode) {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/appointment/addAppointment/`,
          payload,
          { headers: { Authorization: `Bearer ${token}`, "Office-Code": officeCode } }
        );
      } else {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/appointment/${appointmentData.appointmentId}/update`,
          payload,
          { headers: { Authorization: `Bearer ${token}`, "Office-Code": officeCode } }
        );
      }

      notify.success({
        title:   isAddMode ? "Appointment Added" : "Appointment Updated",
        message: `Saved as ${requestType.replace("_", " ")} (${isDropTrailer ? "Drop Trailer" : "Live Load"}).`,
      });
      dispatch(removeTab({ tabIndex: activeTab }));
      dispatch(setActiveTab({ index: 0 }));
      resetForm();
    } catch (error) {
      console.error("Appointment submit error:", error);
      if (error?.response?.status === 409) {
        await refreshAndValidateSelectedSlot();
        notify.error({ title: "Slot Unavailable", message: "The selected slot was just taken. Please choose another." });
        return;
      }
      notify.error({
        title:   isAddMode ? "Add Failed" : "Update Failed",
        message: error?.response?.data?.message || `Failed to ${isAddMode ? "add" : "update"} appointment.`,
      });
    } finally {
      setLoading(false);
    }
  };

  return { loading, handleSubmit };
}